import { type I18n, setupI18n } from "@lingui/core";
import {
	MutationCache,
	notifyManager,
	QueryCache,
	QueryClient,
} from "@tanstack/react-query";
import { createRouter as createTanStackRouter } from "@tanstack/react-router";
import { routerWithQueryClient } from "@tanstack/react-router-with-query";
import { getGlobalStartContext } from "@tanstack/react-start";
import {
	createTRPCClient,
	httpBatchLink,
	httpLink,
	isNonJsonSerializable,
	loggerLink,
	splitLink,
} from "@trpc/client";
import {
	createTRPCOptionsProxy,
	type TRPCOptionsProxy,
} from "@trpc/tanstack-react-query";
import type { PropsWithChildren } from "react";
import { toast } from "sonner";
import { DefaultCatchBoundary } from "~/components/default-catch-boundary";
import { NotFound } from "~/components/not-found";
import { SessionExpiredToast } from "~/components/views/auth/session-expired-form";
import { AudioPlayerProvider } from "~/contexts/audio-player-context";
import { getHeaders } from "~/functions/headers";
import { getOrigin } from "~/functions/origin";
import { parseError } from "~/lib/errors";
import { routerWithLingui } from "~/modules/lingui/plugin";
import { routeTree } from "~/routeTree.gen";
import { TRPCProvider } from "~/trpc/client";
import type { TRPCRouter } from "~/trpc/router";
import { PlaybackProvider } from "./hooks/use-playback";

export interface AppContext {
	i18n: I18n;
	queryClient: QueryClient;
	trpc: TRPCOptionsProxy<TRPCRouter>;
}

export function getRouter() {
	const context = getGlobalStartContext();
	const i18n = context?.i18n ?? setupI18n();
	const origin = getOrigin();

	const queryClient: QueryClient = new QueryClient({
		defaultOptions: {
			queries: {
				refetchOnReconnect: () => !queryClient.isMutating(),
				retry: false,
				staleTime: 60_000,
			},
		},
		mutationCache: new MutationCache({
			onError: (error) => {
				const { message } = parseError(error);

				toast.error(message);
			},
			async onSettled() {
				if (queryClient.isMutating() === 1) {
					// Automatically refresh queries after a mutation
					return queryClient.invalidateQueries();
				}
			},
		}),
		queryCache: new QueryCache({
			onError: async (error) => {
				const { message, code } = parseError(error);

				if (code === 401) {
					toast.message(<SessionExpiredToast />);
				} else {
					toast.error(message, {
						action: {
							// label: i18n._("Retry"),
							label: "Retry",
							onClick: () => {
								queryClient.invalidateQueries();
							},
						},
					});
				}
			},
		}),
	});

	const trpcClient = createTRPCClient<TRPCRouter>({
		links: [
			loggerLink({ enabled: () => import.meta.env.DEV && false }),
			splitLink({
				condition: (op) => isNonJsonSerializable(op.input),
				// Use httpLink for FormData
				true: httpLink({
					url: `${origin}/api/trpc`,
					headers: getHeaders(),
				}),
				false: httpBatchLink({
					url: `${origin}/api/trpc`,
					headers: getHeaders(),
				}),
			}),
		],
	});

	if (typeof document !== "undefined") {
		notifyManager.setScheduler(window.requestAnimationFrame);
	}

	const router = routerWithLingui(
		routerWithQueryClient(
			createTanStackRouter({
				routeTree,
				context: {
					i18n,
					queryClient,
					trpc: createTRPCOptionsProxy({
						client: trpcClient,
						queryClient: queryClient,
					}),
				},
				defaultPreload: "intent",
				defaultViewTransition: true,
				defaultErrorComponent: DefaultCatchBoundary,
				defaultNotFoundComponent: () => <NotFound />,
				defaultStaleTime: 60 * 1000,
				defaultPreloadStaleTime: 60 * 1000,
				scrollRestoration: true,
				Wrap: ({ children }: PropsWithChildren) => {
					return (
						<TRPCProvider trpcClient={trpcClient} queryClient={queryClient}>
							<AudioPlayerProvider>
								<PlaybackProvider>{children}</PlaybackProvider>
							</AudioPlayerProvider>
						</TRPCProvider>
					);
				},
			}),
			queryClient,
		),
		i18n,
	);

	return router;
}

type AppRouter = ReturnType<typeof getRouter>;

declare module "@tanstack/react-router" {
	interface Register {
		router: AppRouter;
	}
}
